import { Message } from './types';

// Every server-mutable primitive on Message. If any differs, the refetched
// object wins; otherwise the previous reference is kept so memo()'d rows bail
// out and Virtuoso leaves their DOM untouched — replacing 10k references at
// once repaints every visible row and reads as flicker.
const COMPARED_FIELDS = [
  'status',
  'failureReason',
  'body',
  'caption',
  'name',
  'transcript',
  'awsLink',
  'sentByName',
  'mimeType',
  'type',
  'size',
  'sha256',
  'langCode',
  'templateName',
  'advertisementId',
] as const;

const unchanged = (old: Message, next: Message): boolean =>
  COMPARED_FIELDS.every(f => (old[f] ?? null) === (next[f] ?? null)) &&
  (old.reaction?.receivedEmoji ?? null) ===
    (next.reaction?.receivedEmoji ?? null) &&
  (old.reaction?.sentEmoji ?? null) === (next.reaction?.sentEmoji ?? null) &&
  !!old.context === !!next.context &&
  (old.context?.wamid ?? null) === (next.context?.wamid ?? null) &&
  // interactive is the one object the backend mutates post-insert without any
  // primitive changing alongside (call terminate appends callEvent/duration)
  (old.interactive === next.interactive ||
    JSON.stringify(old.interactive ?? null) ===
      JSON.stringify(next.interactive ?? null));

// The full-history refetch replaces the whole messages array; reuse previous
// object identities where nothing changed. When the new object wins, carry
// over the optimistic-send refId (the server strips it) so the Virtuoso row
// key (refId || wamid) stays stable and the row is not remounted.
export const reconcileMessages = (
  oldList: Message[] | undefined,
  newList: Message[],
): Message[] => {
  if (!oldList?.length) return newList;
  const byWamid = new Map(oldList.map(m => [m.wamid, m]));
  return newList.map(m => {
    const old = byWamid.get(m.wamid);
    if (!old) return m;
    if (unchanged(old, m)) return old;
    return old.refId && !m.refId ? { ...m, refId: old.refId } : m;
  });
};
